import * as Deferred from "effect/Deferred";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { MAX_ASSET_FILE_BYTES, NativeFileDescriptor } from "@linempo/worker-protocol";
import { workerError } from "./model.js";
/** A native channel has one pending receiver. Unsolicited/duplicate Files are never retained. */
export class NativeFileSlot {
    pending = null;
    closed = false;
    accept(input) {
        if (typeof input !== "object" || input === null || !("file" in input))
            return false;
        const value = input;
        const pending = this.pending;
        if (!pending || pending.received)
            return true;
        const raw = value.descriptor;
        if (!raw || raw.transferId !== pending.transferId)
            return true;
        try {
            const descriptor = Schema.decodeUnknownSync(NativeFileDescriptor)(raw);
            if (descriptor.kind !== pending.kind)
                throw new TypeError("Native file kind mismatch");
            if (!(value.file instanceof Blob) || value.file.size !== descriptor.byteLength)
                throw new TypeError("Native file size mismatch");
            if (descriptor.kind === "asset-output" && descriptor.byteLength > MAX_ASSET_FILE_BYTES)
                throw new TypeError("Asset file exceeds 32 MiB");
            pending.received = true;
            Deferred.doneUnsafe(pending.result, Effect.succeed({ descriptor, file: value.file }));
        }
        catch (error) {
            pending.received = true;
            Deferred.doneUnsafe(pending.result, Effect.fail(workerError("protocol", `Invalid native file: ${String(error)}`)));
        }
        return true;
    }
    receive(kind, transferId, request, timeoutMs) {
        const self = this;
        return Effect.acquireUseRelease(Effect.try({ try: () => {
                if (self.closed)
                    throw workerError("unavailable", "Native file channel is closed");
                if (self.pending)
                    throw workerError("busy", "A native file download is already pending");
                const pending = { kind, transferId, result: Deferred.makeUnsafe(), received: false };
                self.pending = pending;
                return pending;
            }, catch: (error) => error }), (pending) => Effect.gen(function* () {
            const response = yield* request;
            const expected = response.descriptor;
            if (expected.kind !== kind || expected.transferId !== transferId)
                return yield* Effect.fail(workerError("protocol", "RPC native descriptor does not match request"));
            const native = yield* Deferred.await(pending.result);
            if (expected.byteLength !== native.descriptor.byteLength)
                return yield* Effect.fail(workerError("protocol", "RPC and native file sizes differ"));
            return { file: native.file, value: response.value };
        }).pipe(Effect.timeoutOrElse({ duration: timeoutMs, orElse: () => Effect.fail(workerError("timeout", "Native file transfer timed out")) })), (pending) => Effect.sync(() => { if (self.pending === pending)
            self.pending = null; }));
    }
    fail(error) {
        this.closed = true;
        if (this.pending)
            Deferred.doneUnsafe(this.pending.result, Effect.fail(error));
        this.pending = null;
    }
}
